import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { PaginatedSpecializationsActions } from 'application/constants/actions';
import { SpecializationsAPI } from 'integration/api/specializations';
import { fetchPaginatedSpecializationProps } from 'common/types/paginatedSpecializations';
import {
  SpecializationCreationDto,
  SpecializationUpdateDto,
} from '@docbay/schemas';

/**
 * Fetches a paginated list of specializations.
 *
 * @param params - The parameters to fetch the paginated list of specializations with.
 * @returns A promise that resolves to the data returned by the API.
 */
export const fetchPaginatedSpecializations = createAsyncThunk(
  PaginatedSpecializationsActions.fetchPaginatedSpecializations,
  async (params: fetchPaginatedSpecializationProps, { rejectWithValue }) => {
    try {
      const { data } = await SpecializationsAPI.fetchPaginatedSpecializations(
        params,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Adds a specialization.
 *
 * @param params - The parameters to create the new specialization with.
 * @returns A promise that resolves to the data returned by the API.
 */
export const addSpecialization = createAsyncThunk(
  PaginatedSpecializationsActions.addSpecialization,
  async (params: SpecializationCreationDto, { rejectWithValue }) => {
    try {
      const { data } = await SpecializationsAPI.addSpecialization(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deactivates a specialization.
 *
 * @param id - The ID of the specialization to deactivate.
 * @returns A promise that resolves to the data returned by the API.
 */
export const deactivateSpecialization = createAsyncThunk(
  PaginatedSpecializationsActions.deactivateSpecialization,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await SpecializationsAPI.deactivateSpecialization(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Activates a specialization.
 *
 * @param id - The ID of the specialization to activate.
 * @returns A promise that resolves to the data returned by the API.
 */
export const activateSpecialization = createAsyncThunk(
  PaginatedSpecializationsActions.activateSpecialization,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await SpecializationsAPI.activateSpecialization(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Edits a specialization.
 *
 * @param params - The parameters to update the specialization with, including its ID and updated data.
 * @returns A promise that resolves to the data returned by the API.
 */
export const editSpecializations = createAsyncThunk(
  PaginatedSpecializationsActions.editSpecializations,
  async (
    params: { data: SpecializationUpdateDto; id: string },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await SpecializationsAPI.editSpecialization(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
